/** Must stay in sync with backend SessionTurnService::MAX_QUEUE_DEPTH */
export const MAX_QUEUE_DEPTH = 100;

/** Mirrors backend GithubAppInstallation::CODESMITH_ORG_BLOCKED_MESSAGE. */
export const CODESMITH_ORG_BLOCKED_MESSAGE =
  "[code]smith is unavailable while your organization's Blacksmith account is blocked. Please contact Blacksmith support to restore access.";

// Synthetic, client-only session ids. Real assistant sessions are persisted
// under a UUID; these stand-ins are rendered before a session row exists:
// - `draft:<uuid>` ids (see `NEW_SESSION_DRAFT_ID_PREFIX`) back
//   `NewSessionView`'s composer before the user sends. Each id is a distinct
//   client-side draft entity whose text persists in localStorage and surfaces
//   as a draft row in the session sidebar.
// - `DRAFT_SESSION_ID` is the legacy single-draft id; it only survives as the
//   localStorage key migrated into a `draft:<uuid>` entity on load.
// The backend validates `session_id` as `nullable|uuid`, so these ids must
// never be sent to session-scoped endpoints (they would 422). Keep them here
// as the single source of truth so producers and consumers agree.
// Mid-create chats use a client-minted real UUID + `status: "creating"` shell
// (see `buildClientSessionShell`); skills fetches must stay org-scoped until
// create acks (ChatInputArea / ChatPane gate on `status === "creating"`).
export const DRAFT_SESSION_ID = "draft";

/** Prefix for client-side new-session draft entity ids (`draft:<uuid>`). */
export const NEW_SESSION_DRAFT_ID_PREFIX = `${DRAFT_SESSION_ID}:`;

/** True when `id` is a client-side new-session draft entity id. */
export function isNewSessionDraftId(id: string | null | undefined): boolean {
  return id != null && id.startsWith(NEW_SESSION_DRAFT_ID_PREFIX);
}

/**
 * The bare `<uuid>` key id of a draft entity id, accepting either form
 * (`draft:<uuid>` or `<uuid>`). Every consumer that turns an external draft
 * reference (sidebar row id, `?draft=` URL param) into a composer key id must
 * normalize through this so the prefix can never be applied twice.
 */
export function newSessionDraftKeyId(id: string): string {
  return id.startsWith(NEW_SESSION_DRAFT_ID_PREFIX)
    ? id.slice(NEW_SESSION_DRAFT_ID_PREFIX.length)
    : id;
}

const SYNTHETIC_SESSION_IDS: ReadonlySet<string> = new Set([DRAFT_SESSION_ID]);

/** True when `id` is a synthetic, non-persisted session id. */
export function isSyntheticSessionId(id: string | null | undefined): boolean {
  return (
    id != null && (SYNTHETIC_SESSION_IDS.has(id) || isNewSessionDraftId(id))
  );
}
